import { useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { adminInviteToSharedPool, adminSetDeliveryPayConfig, adminSetSharedDeliveryPricing } from '../api';

// Replaces the three cramped inline cells that used to sit in the admin Users table
// (DeliveryPoolCell / DeliveryPayCell / SharedDeliveryPricingCell). Everything that was an
// unlabelled number box is now a labelled field with a one-line explanation and the
// "leave blank = platform default" rule spelled out.
//
// Sections shown depend on the account's roles:
//   delivery / agent -> shared-pool status + invite, and the per-person delivery fee override
//   owner            -> what this business charges OTHER businesses for using its shared people
//
// `poolKind` tailors wording: 'driver' (car), 'delivery person' (jewelry), 'agent' (real_estate),
// 'reservation manager' (hospitality). `showPool` / `showB2B` let a vertical drop sections it
// doesn't use (real_estate: fee only).
export default function DeliveryPoolPricingModal({
  user, show, onHide, onChanged,
  poolKind = 'delivery person', showPool = true, showB2B = true
}) {
  const roles = user ? (user.effectiveRoles || [user.role]) : [];
  const isField = roles.includes('delivery');
  const isOwner = roles.includes('owner');

  const [err, setErr] = useState('');
  const [savingPool, setSavingPool] = useState(false);
  const [savingFee, setSavingFee] = useState(false);
  const [savingB2B, setSavingB2B] = useState(false);
  const [saved, setSaved] = useState('');

  const pay = user?.deliveryPay || {};
  const [sharePct, setSharePct] = useState(pay.shareRate != null ? String(Math.round(pay.shareRate * 100)) : '');
  const [minFee, setMinFee] = useState(pay.minFee != null ? String(pay.minFee) : '');

  const b2b = user?.sharedDeliveryPricing || {};
  const [base, setBase] = useState(b2b.baseFeePerLeg != null ? String(b2b.baseFeePerLeg) : '');
  const [failed, setFailed] = useState(b2b.failedAttemptFee != null ? String(b2b.failedAttemptFee) : '');
  const [wait, setWait] = useState(b2b.waitingRatePer15Min != null ? String(b2b.waitingRatePer15Min) : '');

  if (!user) return null;

  const flash = (msg) => { setSaved(msg); setErr(''); onChanged?.(); setTimeout(() => setSaved(''), 2500); };
  const fail = (e, fallback) => setErr(e.response?.data?.msg || fallback);

  const invite = async () => {
    setSavingPool(true); setErr('');
    try { await adminInviteToSharedPool(user._id); flash('Invitation sent.'); }
    catch (e) { fail(e, 'Could not send the invite.'); }
    finally { setSavingPool(false); }
  };

  const saveFee = async () => {
    setSavingFee(true); setErr('');
    try {
      await adminSetDeliveryPayConfig(user._id, {
        shareRate: sharePct === '' ? null : Math.max(0, Math.min(100, Number(sharePct))) / 100,
        minFee: minFee === '' ? null : Math.max(0, Number(minFee))
      });
      flash('Fee saved.');
    } catch (e) { fail(e, 'Could not save the fee.'); }
    finally { setSavingFee(false); }
  };

  const saveB2B = async () => {
    setSavingB2B(true); setErr('');
    try {
      await adminSetSharedDeliveryPricing(user._id, {
        baseFeePerLeg: base === '' ? null : Math.max(0, Number(base)),
        failedAttemptFee: failed === '' ? null : Math.max(0, Number(failed)),
        waitingRatePer15Min: wait === '' ? null : Math.max(0, Number(wait))
      });
      flash('Charges saved.');
    } catch (e) { fail(e, 'Could not save the charges.'); }
    finally { setSavingB2B(false); }
  };

  const poolStatus = () => {
    if (!user.onboardedByOwnerId) return <Badge bg="secondary">Platform account — every owner can assign them</Badge>;
    if (user.availableForOtherOwners) return <Badge bg="info" text="dark">Shared pool — available to other businesses</Badge>;
    const st = user.sharingInvite?.status;
    if (st === 'pending') return <Badge bg="warning" text="dark">Private — invitation pending the {poolKind}&apos;s reply</Badge>;
    if (st === 'declined') return <Badge bg="secondary">Private — the {poolKind} declined the last invitation</Badge>;
    return <Badge bg="secondary">Private to {user.onboardedByOwnerName || 'one business'}</Badge>;
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Pool &amp; pricing — {user.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {err && <Alert variant="danger" className="py-2">{err}</Alert>}
        {saved && <Alert variant="success" className="py-2">{saved}</Alert>}

        {showPool && isField && (
          <section className="mb-4">
            <h6 className="text-uppercase text-muted small">Shared pool</h6>
            <p className="small mb-2">
              A privately-onboarded {poolKind} works only for the business that added them. Inviting
              them to the shared pool lets any business on the platform assign them (they must
              accept the invitation from their own dashboard).
            </p>
            <div className="mb-2">{poolStatus()}</div>
            {user.onboardedByOwnerId && !user.availableForOtherOwners && user.sharingInvite?.status !== 'pending' && (
              <Button size="sm" disabled={savingPool} onClick={invite}>
                {savingPool && <Spinner as="span" size="sm" className="me-1" />}
                {user.sharingInvite?.status === 'declined' ? 'Re-invite to shared pool' : 'Invite to shared pool'}
              </Button>
            )}
          </section>
        )}

        {isField && (
          <section className="mb-4">
            <h6 className="text-uppercase text-muted small">Per-{poolKind} fee</h6>
            <p className="small mb-2">
              What this {poolKind} earns per delivery leg. Leave a field blank to use the
              platform default.
            </p>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-0">Share of the delivery charge (%)</Form.Label>
              <Form.Control
                type="number" size="sm" min="0" max="100" style={{ maxWidth: 160 }}
                placeholder="platform default"
                value={sharePct} onChange={(e) => setSharePct(e.target.value)}
              />
              <Form.Text muted>e.g. 70 means they keep 70% of what the customer paid for delivery.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-0">Minimum fee per leg (₹)</Form.Label>
              <Form.Control
                type="number" size="sm" min="0" style={{ maxWidth: 160 }}
                placeholder="platform default"
                value={minFee} onChange={(e) => setMinFee(e.target.value)}
              />
              <Form.Text muted>Paid even when the percentage works out lower (e.g. a very short trip).</Form.Text>
            </Form.Group>
            <Button size="sm" disabled={savingFee} onClick={saveFee}>
              {savingFee && <Spinner as="span" size="sm" className="me-1" />}Save fee
            </Button>
          </section>
        )}

        {showB2B && isOwner && (
          <section>
            <h6 className="text-uppercase text-muted small">Charges to other businesses</h6>
            <p className="small mb-2">
              When another business uses one of <strong>your</strong> shared delivery people, this is
              what you bill them. Leave a field blank to use the platform default.
            </p>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-0">Base fee per delivery leg (₹)</Form.Label>
              <Form.Control type="number" size="sm" min="0" style={{ maxWidth: 160 }}
                placeholder="platform default" value={base} onChange={(e) => setBase(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-0">Failed-delivery attempt fee (₹)</Form.Label>
              <Form.Control type="number" size="sm" min="0" style={{ maxWidth: 160 }}
                placeholder="platform default" value={failed} onChange={(e) => setFailed(e.target.value)} />
              <Form.Text muted>Charged when the delivery person turns up but can&apos;t hand over.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="small mb-0">Waiting charge per 15 min (₹)</Form.Label>
              <Form.Control type="number" size="sm" min="0" style={{ maxWidth: 160 }}
                placeholder="platform default" value={wait} onChange={(e) => setWait(e.target.value)} />
            </Form.Group>
            <Button size="sm" disabled={savingB2B} onClick={saveB2B}>
              {savingB2B && <Spinner as="span" size="sm" className="me-1" />}Save charges
            </Button>
          </section>
        )}

        {!isField && !isOwner && (
          <p className="text-muted small mb-0">This account has no pool or delivery-pricing settings.</p>
        )}
      </Modal.Body>
    </Modal>
  );
}

// Compact read-only summary for the Users-table cell. `opts.pool: false` drops the pool-status
// word for verticals that have no shared-pool concept (hospitality, gym).
export function poolPricingSummary(user, poolKind = 'delivery person', opts = {}) {
  const roles = user.effectiveRoles || [user.role];
  const bits = [];
  if (roles.includes('delivery')) {
    if (opts.pool !== false) {
      if (!user.onboardedByOwnerId) bits.push('Platform account');
      else if (user.availableForOtherOwners) bits.push('Shared pool');
      else bits.push('Private');
    }
    const p = user.deliveryPay || {};
    if (p.shareRate != null || p.minFee != null) {
      const parts = [];
      if (p.shareRate != null) parts.push(`${Math.round(p.shareRate * 100)}%`);
      if (p.minFee != null) parts.push(`min ₹${p.minFee}`);
      bits.push(`Fee: ${parts.join(' / ')}`);
    } else {
      bits.push('Fee: platform default');
    }
  }
  if (roles.includes('owner')) {
    const b = user.sharedDeliveryPricing || {};
    if (b.baseFeePerLeg != null || b.failedAttemptFee != null || b.waitingRatePer15Min != null) {
      bits.push(`B2B: ₹${b.baseFeePerLeg ?? '–'} base / ₹${b.failedAttemptFee ?? '–'} failed / ₹${b.waitingRatePer15Min ?? '–'} wait`);
    } else {
      bits.push('B2B: platform default');
    }
  }
  return bits.join(' · ');
}
